import React from "react";
import { Link } from "react-router-dom";
import img1 from "../image/gallery/extrusion.jpeg";
import img3 from "../image/warehouse.png";
import img5 from "../image/safety.png";
import labImage from '../image/lab.png';

function Infrastructure() {
  return (
    <div className="w-full bg-slate-50/50 overflow-hidden">

      {/* Premium Hero Banner Section */}
      <div className="w-full relative bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 pt-20 pb-28 md:pt-24 md:pb-36 lg:pt-28 lg:pb-40 overflow-hidden">

        {/* Ambient Hero Glow Effects */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 text-center text-white relative z-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-4 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
            <span className="text-xs font-bold uppercase tracking-wider text-orange-50">Industrial Facility</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight drop-shadow-md mb-4">
            Our Infrastructure
          </h1>
          <p className="text-sm sm:text-base md:text-lg max-w-3xl mx-auto leading-relaxed font-normal text-orange-50 opacity-95">
            PoloPipes operates a state-of-the-art manufacturing ecosystem equipped with high-precision extrusion machinery, automated testing labs, and streamlined logistics infrastructure.
          </p>
        </div>

        {/* Curved Divider Path */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-[0] z-10">
          <svg
            className="relative block w-full h-[60px] md:h-[100px]"
            viewBox="0 0 1920 120"
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,120 C640,30 1280,30 1920,120 L1920,120 L0,120 Z"
              fill="#f8fafc"
            />
          </svg>
        </div>
      </div>

      {/* Breadcrumb Navigation Bar */}
      <div className="border-b border-slate-200/80 bg-white/80 backdrop-blur-md py-3.5 px-6 sm:px-12 relative z-20">
        <div className="max-w-7xl mx-auto flex items-center space-x-2 text-xs sm:text-sm font-medium text-slate-500">
          <Link to="/" className="text-orange-600 hover:text-orange-700 hover:underline transition-colors">Home</Link>
          <span className="text-slate-300 font-light">&gt;</span>
          <span className="text-slate-800">Infrastructure</span>
        </div>
      </div>

      {/* Dynamic Content Showcase Section */}
      <section className="py-20 md:py-28 relative">

        {/* Background Ambient Blur Orbs */}
        <div className="absolute top-1/4 -left-40 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-24 md:space-y-32 relative z-10">

          {/* Row 1: Production Excellence */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">

            {/* Visual Column */}
            <div className="lg:col-span-6 order-first lg:order-last">
              <div className="group relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-200/60 border border-slate-200/80 bg-white p-2.5 transition-all duration-500 hover:-translate-y-1">
                <div className="relative rounded-2xl overflow-hidden h-[260px] sm:h-[360px] md:h-[400px]">
                  <img
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    src={img1}
                    alt="Production Extrusion Lines"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent opacity-80"></div>
                  <span className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-md text-slate-900 text-xs font-bold px-3 py-1.5 rounded-xl border border-white/20 shadow-sm">
                    Automated Lines
                  </span>
                </div>
              </div>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-6 flex flex-col justify-center space-y-6">
              <div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-orange-100 text-orange-700 mb-2">
                  Manufacturing Power
                </span>
                <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                  Production Excellence
                </h2>
              </div>

              <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">

                <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:border-orange-200 transition-colors">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center font-bold shrink-0 mt-0.5 border border-orange-100">
                      01
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-bold text-base mb-1">Advanced Extrusion Lines</h3>
                      <p className="text-xs sm:text-sm text-slate-600">
                        High-capacity extrusion machinery designed for high output, precise wall thickness control, and uniform dimensional stability.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:border-orange-200 transition-colors">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center font-bold shrink-0 mt-0.5 border border-orange-100">
                      02
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-bold text-base mb-1">Automated Compounding Stations</h3>
                      <p className="text-xs sm:text-sm text-slate-600">
                        High-speed blending units ensure precise mixing of resin formulations, heat stabilizers, and colorants prior to production.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:border-orange-200 transition-colors">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center font-bold shrink-0 mt-0.5 border border-orange-100">
                      03
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-bold text-base mb-1">Precision Belling & Cutting</h3>
                      <p className="text-xs sm:text-sm text-slate-600">
                        Automated cutting and socket-belling machinery engineered for smooth, leak-proof joint connections across all pipe diameters.
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>

          {/* Row 2: Quality Control */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">

            {/* Visual Column */}
            <div className="lg:col-span-6">
              <div className="group relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-200/60 border border-slate-200/80 bg-white p-2.5 transition-all duration-500 hover:-translate-y-1">
                <div className="relative rounded-2xl overflow-hidden h-[260px] sm:h-[360px] md:h-[400px]">
                  <img
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    src={labImage}
                    alt="Testing Laboratories"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent opacity-80"></div>
                  <span className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-md text-slate-900 text-xs font-bold px-3 py-1.5 rounded-xl border border-white/20 shadow-sm">
                    BIS Compliant Testing
                  </span>
                </div>
              </div>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-6 flex flex-col justify-center space-y-6">
              <div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-sky-100 text-sky-800 mb-2">
                  Quality Compliance
                </span>
                <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                  Quality at Every Step
                </h2>
              </div>

              <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">

                <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:border-sky-200 transition-colors">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold shrink-0 mt-0.5 border border-sky-100">
                      01
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-bold text-base mb-1">Testing Laboratories</h3>
                      <p className="text-xs sm:text-sm text-slate-600">
                        Equipped for hydrostatic pressure testing, impact testing, heat reversion, and tensile property evaluation under strict BIS guidelines.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:border-sky-200 transition-colors">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold shrink-0 mt-0.5 border border-sky-100">
                      02
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-bold text-base mb-1">In-Line Quality Audits</h3>
                      <p className="text-xs sm:text-sm text-slate-600">
                        Continuous dimensional inspection and physical checking during production cycles to catch batch discrepancies instantly.
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>

          {/* Row 3: Efficient Warehousing */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">

            {/* Visual Column */}
            <div className="lg:col-span-6 order-first lg:order-last">
              <div className="group relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-200/60 border border-slate-200/80 bg-white p-2.5 transition-all duration-500 hover:-translate-y-1">
                <div className="relative rounded-2xl overflow-hidden h-[260px] sm:h-[360px] md:h-[400px]">
                  <img
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    src={img3}
                    alt="Raw Material and Finished Goods Warehousing"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent opacity-80"></div>
                  <span className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-md text-slate-900 text-xs font-bold px-3 py-1.5 rounded-xl border border-white/20 shadow-sm">
                    Supply Chain Hub
                  </span>
                </div>
              </div>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-6 flex flex-col justify-center space-y-6">
              <div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-emerald-100 text-emerald-800 mb-2">
                  Smart Logistics
                </span>
                <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                  Efficient Warehousing
                </h2>
              </div>

              <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">

                <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:border-emerald-200 transition-colors">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold shrink-0 mt-0.5 border border-emerald-100">
                      01
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-bold text-base mb-1">Raw Material Depots</h3>
                      <p className="text-xs sm:text-sm text-slate-600">
                        Climate-controlled storage space for polymer resins and additives to prevent moisture contamination and preserve compounding consistency.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:border-emerald-200 transition-colors">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold shrink-0 mt-0.5 border border-emerald-100">
                      02
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-bold text-base mb-1">Finished Goods Yard</h3>
                      <p className="text-xs sm:text-sm text-slate-600">
                        Categorized racking systems designed for seamless inventory retrieval, rapid loading workflows, and nationwide dispatch efficiency.
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>

          {/* Row 4: Safety & Sustainability */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">

            {/* Visual Column */}
            <div className="lg:col-span-6">
              <div className="group relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-200/60 border border-slate-200/80 bg-white p-2.5 transition-all duration-500 hover:-translate-y-1">
                <div className="relative rounded-2xl overflow-hidden h-[260px] sm:h-[360px] md:h-[400px]">
                  <img
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    src={img5}
                    alt="Safety and Environment Systems"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent opacity-80"></div>
                  <span className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-md text-slate-900 text-xs font-bold px-3 py-1.5 rounded-xl border border-white/20 shadow-sm">
                    ESG Standards
                  </span>
                </div>
              </div>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-6 flex flex-col justify-center space-y-6">
              <div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-amber-100 text-amber-800 mb-2">
                  Responsible Operations
                </span>
                <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                  Safety & Sustainability
                </h2>
              </div>

              <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">

                <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:border-amber-200 transition-colors">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold shrink-0 mt-0.5 border border-amber-100">
                      01
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-bold text-base mb-1">Occupational Safety First</h3>
                      <p className="text-xs sm:text-sm text-slate-600">
                        Enforced protective equipment protocols, emergency safety systems, and regular operational hazards training for all staff.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:border-amber-200 transition-colors">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold shrink-0 mt-0.5 border border-amber-100">
                      02
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-bold text-base mb-1">Zero Plastic Waste Recycling</h3>
                      <p className="text-xs sm:text-sm text-slate-600">
                        In-house regrind processing units ensure production scrap is recycled directly back into sustainable product manufacturing.
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>

        </div>
      </section>

    </div>
  );
}

export default Infrastructure;